import React from 'react';
import { Stack, Typography, useTheme, Box } from '@gogame/components';
import { Difference, CellValue, ValueWrapper } from '@gogame/data-grid';
import { NumberValueFormatter } from '@gogame/intl-ts';

function getValueFormatter(type) {
  switch (type) {
    case 'percent':
      return (value) => <NumberValueFormatter.Percent value={value} />;
    case 'currency':
      return (value) => <NumberValueFormatter.Currency value={value} />;
    default:
      return (value) => value;
  }
}

function getAlign(type) {
  switch (type) {
    case 'percent':
    case 'currency':
      return 'right';
    default:
      return 'left';
  }
}

function isDefined(value) {
  return value !== undefined && value !== null;
}

function ifDefined(value, formatter) {
  return isDefined(value) ? formatter(value) : value;
}

export const Metric = ({
  type = 'percent',
  label,
  value,
  isHighlightLower,
}) => {
  const formatter = getValueFormatter(type);
  const align = getAlign(type);
  const withCompare = isDefined(value.compareValue);

  return (
    <Stack
      className={'GgMetric'}
      sx={{
        padding: '2px 8px',
        borderRadius: '2px',
        justifyContent: 'space-between',
      }}
      direction={'row'}
    >
      <Box>
        <Typography variant={'body1'} noWrap>
          {label}
        </Typography>
        {value.difference ? (
          <Difference
            value={value.difference}
            direction={'row'}
            justifyContent={'start'}
            alignItems={'center'}
            isHighlightLower={isHighlightLower}
          />
        ) : null}
      </Box>
      <Box>
        <ValueWrapper align={'right'}>
          <CellValue
            value={ifDefined(value.value, formatter)}
            lineClamp={1}
            align={align}
          />
          {withCompare ? (
            <CellValue
              value={ifDefined(value.compareValue, formatter)}
              lineClamp={1}
              align={align}
              mt={4}
            />
          ) : null}
        </ValueWrapper>
      </Box>
    </Stack>
  );
};

const MetricList = ({
  children,
  data = {
    test: {
      value: 12,
      compareValue: 12,
      difference: 12,
    },
  },
}) => {
  const theme = useTheme();

  return (
    <Stack
      sx={{
        ...theme.typography.body1,
        margin: '0 -8px',
        '& .GgMetric': {
          '&:hover': {
            textDecoration: 'none',
            backgroundColor: theme.palette.action.hover,
            // Reset on touch devices, it doesn't add specificity
            '@media (hover: none)': {
              backgroundColor: 'transparent',
            },
          },
        },
      }}
    >
      {React.Children.toArray(children)
        .filter((child) => child.type === Metric)
        .map((child) =>
          React.cloneElement(child, {
            value: data[child.props.field],
            key: child.props.field,
          })
        )}
    </Stack>
  );
};

export default MetricList;
